// Currency conversion utility
// Exchange rates (base: USD = 1.0)
// Note: These are approximate rates. For production, consider using a real-time API like exchangerate-api.com

export type CurrencyCode = 'USD' | 'AUD' | 'NRS' | 'INR' | 'JPY';

export interface Currency {
  code: CurrencyCode;
  name: string;
  symbol: string;
}

export const currencies: Currency[] = [
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'NRS', name: 'Nepalese Rupee', symbol: '₨' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
];

// Exchange rates relative to USD (1 USD = X currency)
const exchangeRates: Record<CurrencyCode, number> = {
  USD: 1.0,
  AUD: 1.52, // 1 USD = 1.52 AUD (approximate)
  NRS: 133.5, // 1 USD = 133.5 NRS (approximate)
  INR: 83.0, // 1 USD = 83 INR (approximate)
  JPY: 150.0, // 1 USD = 150 JPY (approximate)
};

/**
 * Convert price from USD to target currency
 * @param priceInUSD - Price in USD
 * @param targetCurrency - Target currency code
 * @returns Converted price
 */
export function convertCurrency(priceInUSD: number | null | undefined, targetCurrency: CurrencyCode): number {
  // Ensure price is a valid number
  const numPrice = typeof priceInUSD === 'number' ? priceInUSD : (priceInUSD ? parseFloat(String(priceInUSD)) : 0);
  const validPrice = isNaN(numPrice) || numPrice < 0 ? 0 : numPrice;

  if (targetCurrency === 'USD') {
    return validPrice;
  }

  const rate = exchangeRates[targetCurrency];
  if (!rate) {
    console.warn(`Exchange rate not found for ${targetCurrency}, using USD`);
    return validPrice;
  }

  return validPrice * rate;
}

/**
 * Convert price from any currency back to USD
 * @param price - Price in source currency
 * @param sourceCurrency - Source currency code
 * @returns Price in USD
 */
export function convertToUSD(price: number, sourceCurrency: CurrencyCode): number {
  if (sourceCurrency === 'USD') {
    return price;
  }

  const rate = exchangeRates[sourceCurrency];
  if (!rate) {
    console.warn(`Exchange rate not found for ${sourceCurrency}, using USD`);
    return price;
  }

  return price / rate;
}

/**
 * Format price with currency symbol
 * @param price - Price value
 * @param currency - Currency code
 * @param decimals - Number of decimal places (default: 0 for JPY, 2 for others)
 * @returns Formatted price string
 */
export function formatPriceWithCurrency(
  price: number | null | undefined,
  currency: CurrencyCode,
  decimals?: number
): string {
  // Ensure price is a valid number
  const numPrice = typeof price === 'number' ? price : (price ? parseFloat(String(price)) : 0);
  const validPrice = isNaN(numPrice) || numPrice < 0 ? 0 : numPrice;

  const currencyInfo = currencies.find(c => c.code === currency);
  const symbol = currencyInfo?.symbol || '$';

  // JPY typically doesn't use decimals
  const decimalPlaces = decimals !== undefined
    ? decimals
    : currency === 'JPY' ? 0 : 2;

  const formattedPrice = validPrice.toFixed(decimalPlaces);

  // For currencies like INR, NRS, place symbol before number
  // For JPY, place symbol before number
  if (currency === 'INR' || currency === 'NRS' || currency === 'JPY') {
    return `${symbol}${formattedPrice}`;
  }

  // For USD and AUD, place symbol before number
  return `${symbol}${formattedPrice}`;
}

/**
 * Get currency info by code
 */
export function getCurrencyInfo(code: CurrencyCode): Currency | undefined {
  return currencies.find(c => c.code === code);
}

